import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Projects.css';

gsap.registerPlugin(ScrollTrigger);

const projects = [
  {
    title: 'Student Attendance Tracking',
    description: 'Full-stack attendance management platform with real-time tracking, role-based dashboards, and automated reporting built with React and Node.js.',
    tech: ['React', 'Node.js', 'MySQL', 'Next.js'],
    link: 'https://cpp.patilpratik.in/',
    gradient: 'linear-gradient(135deg, #6c63ff 0%, #00d4ff 100%)',
    accentColor: '#6c63ff',
    icon: '📊',
    year: '2025',
  },
  {
    title: 'eBook Marketplace',
    description: 'A Razorpay-integrated eBook marketplace with secure checkout, real-time order updates, and a clean buyer/seller experience.',
    tech: ['React', 'NestJS', 'MongoDB', 'Razorpay'],
    link: 'https://ebook.patilpratik.in/',
    gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
    accentColor: '#ff6b6b',
    icon: '📚',
    year: '2025',
  },
  {
    title: 'Universal Translator',
    description: 'A sleek web app that translates text across 50+ languages in real-time with a minimal, fast interface and keyboard shortcuts.',
    tech: ['React', 'HTML', 'CSS', 'JavaScript'],
    link: 'https://translator.patilpratik.in/',
    gradient: 'linear-gradient(135deg, #00d4ff 0%, #6c63ff 100%)',
    accentColor: '#00d4ff',
    icon: '🌐',
    year: '2025',
  },
];

function ProjectCard({ project, index }) {
  const cardRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    gsap.set(cardRef.current, { opacity: 0, y: 60, scale: 0.96 });
    gsap.to(cardRef.current, {
      opacity: 1, y: 0, scale: 1,
      duration: 0.8, ease: 'power3.out',
      delay: index * 0.15,
      scrollTrigger: {
        trigger: cardRef.current,
        start: 'top 88%',
        toggleActions: 'play none none none',
      },
    });
  }, [index]);

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    gsap.to(cardRef.current, {
      rotateX: -y * 0.5,
      rotateY: x * 0.5,
      duration: 0.4,
      ease: 'power2.out',
      transformPerspective: 800,
    });
  };

  const handleMouseLeave = () => {
    gsap.to(cardRef.current, {
      rotateX: 0, rotateY: 0,
      duration: 0.6, ease: 'elastic.out(1, 0.8)',
    });
    setHovered(false);
  };

  return (
    <a
      ref={cardRef}
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="project-card"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div className="project-accent-bar" style={{ background: project.gradient }} />
      <div className="project-body">
        <div className="project-header">
          <div className="project-icon" style={{ background: `${project.accentColor}18`, border: `1px solid ${project.accentColor}30` }}>
            {project.icon}
          </div>
          <div className="project-meta">
            <span className="project-year" style={{ color: project.accentColor }}>{project.year}</span>
            <span className="project-arrow" style={{ transform: hovered ? 'translate(3px,-3px)' : 'none' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </span>
          </div>
        </div>
        <h3 className="project-title">{project.title}</h3>
        <p className="project-desc">{project.description}</p>
        <div className="project-tags">
          {project.tech.map((t) => (
            <span key={t} className="project-tag" style={{ color: project.accentColor, borderColor: `${project.accentColor}35`, background: `${project.accentColor}0f` }}>
              {t}
            </span>
          ))}
        </div>
      </div>
      <div className="project-glow" style={{ background: `radial-gradient(ellipse at center, ${project.accentColor}15 0%, transparent 70%)`, opacity: hovered ? 1 : 0 }} />
    </a>
  );
}

function Projects() {
  const sectionRef = useRef(null);
  const labelRef   = useRef(null);
  const titleRef   = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set([labelRef.current, titleRef.current], { opacity: 0 });
      gsap.timeline({
        scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none none' },
      })
      .to(labelRef.current, { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' })
      .to(titleRef.current, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.3');
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="projects" className="projects" ref={sectionRef}>
      <div className="container">
        <div ref={labelRef} className="section-label">What I've Built</div>
        <h2 ref={titleRef} className="section-title">
          Featured <span className="gradient-text">Projects</span>
        </h2>
        <p className="section-subtitle">Real projects, shipped and live — built to solve real problems.</p>

        <div className="projects-grid">
          {projects.map((project, i) => (
            <ProjectCard key={project.title} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;
